import { useState, useEffect } from 'react';
import { AutocompleteArrayInput, useInput, useRecordContext } from 'react-admin';
import { FormControlLabel, Checkbox, Box } from '@mui/material';
import { CATALOG_AVAILABILITY_OPTIONS } from '@src/types';

interface AvailabilityInputProps {
  source: string;
  label: string;
}

/**
 * Custom availability input that shows a checkbox when availability is inconsistent
 * across variants, allowing the user to make them consistent.
 */
const AvailabilityInput = ({ source, label }: AvailabilityInputProps) => {
  const record = useRecordContext();
  const isInconsistentAvailability = record?.isInconsistentAvailability || false;

  // State to track if user wants to make availabilities consistent
  const [makeConsistent, setMakeConsistent] = useState(false);

  // Get the makeAvailabilitiesConsistent field input hook
  const { field: makeConsistentField } = useInput({
    source: 'makeAvailabilitiesConsistent',
  });

  const availabilityChoices = CATALOG_AVAILABILITY_OPTIONS.map(option => ({
    id: option,
    name: option,
  }));

  // Sync the checkbox state with the form field
  useEffect(() => {
    makeConsistentField.onChange(makeConsistent);
  }, [makeConsistent, makeConsistentField]);

  // Reset makeConsistent when record changes (e.g., after save)
  useEffect(() => {
    if (!isInconsistentAvailability) {
      setMakeConsistent(false);
    }
  }, [isInconsistentAvailability]);

  return (
    <Box>
      <AutocompleteArrayInput
        source={source}
        label={label}
        choices={availabilityChoices}
        fullWidth
        onChange={() => {
          // When availability changes, set makeConsistent to true if inconsistent
          if (isInconsistentAvailability) {
            setMakeConsistent(true);
          }
        }}
      />
      {isInconsistentAvailability && (
        <FormControlLabel
          control={
            <Checkbox
              checked={makeConsistent}
              onChange={e => setMakeConsistent(e.target.checked)}
            />
          }
          label="The availability reflected above differs for certain child variants of this parent product. Click checkbox to make data consistent across all child variants."
          sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
      )}
    </Box>
  );
};

export default AvailabilityInput;
